import React, { useState, useEffect, useRef } from 'react';
import { faPaperPlane, faStop, faPaperclip, faXmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ACCEPT_ATTRIBUTE } from './uploadRules';

/**
 * The message box. In an Office Optimizer chat for one practice it also carries a
 * paperclip (SS-4267): `canAttach` shows it, `onAttach(file)` is called with the picked
 * file (the parent checks and keeps it), `attachment` is what is attached right now
 * ({name, sending}) and `onRemoveAttachment` clears it. A message can then be sent with a
 * file and no text. While it is `sending`, the chip says so and Stop cancels it.
 */
function InputArea({
  onSend, onStop, questionText, setQuestionText, disableSendButton, isStreaming, placeholder = "Type your question...",
  canAttach = false, attachment = null, onAttach, onRemoveAttachment,
}) {
  const [input, setInput] = useState('');
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setInput(questionText);
  }, [questionText]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() || attachment) {
      onSend(input);
      setInput('');
      setQuestionText('');
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    setQuestionText(e.target.value);
  };

  const handlePick = (e) => {
    const file = e.target.files && e.target.files[0];
    // the same file can be picked again after it was removed or refused
    e.target.value = '';
    if (file && onAttach) onAttach(file);
  };

  const attachDisabled = isStreaming || disableSendButton;

  const sendOrStop = isStreaming ? (
    <button
      className="send-button custom_send_button stop-button"
      onClick={onStop}
      title="Stop generating"
    >
      <FontAwesomeIcon icon={faStop} />
    </button>
  ) : (
    <button
      className={`send-button custom_send_button ${disableSendButton ? 'disabled' : ''}`}
      onClick={handleSubmit}
      disabled={disableSendButton}
    >
      <FontAwesomeIcon icon={faPaperPlane} />
    </button>
  );

  return (
    <div className="input-area custom_input_area">
      <textarea
        ref={textareaRef}
        type="text"
        value={input}
        name="question"
        onChange={handleInputChange}
        placeholder={placeholder}
      />
      {canAttach ? (
        <div className="input-area-toolbar">
          <button
            type="button"
            className={`attach-button ${attachDisabled ? 'disabled' : ''}`}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={attachDisabled}
            title="Attach a CSV, Word (.docx) or PDF file"
            aria-label="Attach a file"
            data-testid="attach-file-button"
          >
            <FontAwesomeIcon icon={faPaperclip} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTRIBUTE}
            onChange={handlePick}
            hidden
            data-testid="attach-file-input"
          />
          {attachment && (
            <span
              className={`attachment-chip ${attachment.sending ? 'sending' : ''}`}
              data-testid="attachment-chip"
              aria-busy={attachment.sending ? 'true' : undefined}
            >
              {attachment.sending && <FontAwesomeIcon icon={faSpinner} spin className="attachment-chip-spinner" />}
              <span className="attachment-chip-name" title={attachment.name}>{attachment.name}</span>
              {attachment.sending ? (
                <span className="attachment-chip-status" role="status">Sending…</span>
              ) : (
                <button
                  type="button"
                  className="attachment-chip-remove"
                  onClick={onRemoveAttachment}
                  disabled={attachDisabled}
                  aria-label={`Remove ${attachment.name}`}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
            </span>
          )}
          {sendOrStop}
        </div>
      ) : sendOrStop}
    </div>
  );
}

export default InputArea;
